import React, { useState, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useGoogleReCaptcha } from "@wojtekmaj/react-recaptcha-v3";

import {
  Root,
  Container,
  Column,
  Image,
  Subtitle,
  Form,
  Divider,
  DistributorContainer,
  DistributorList,
  DistributorHeader,
  DistributorTitle,
  DistributorTooltip,
  DistributorRemoveButton,
  SuccessContainer,
  Note,
} from "./EducationSessionRequest.style";

import imageSrc from "../../assets/images/product-knowledge-request-form-art.jpg";

import { Typography } from "../../ui/Typography/Typography";
import { TextInput } from "../../ui/TextInput/TextInput";
import { Select } from "../../ui/Select/Select";
import { Textarea } from "../../ui/Textarea/Textarea";
import { Checkbox } from "../../ui/Checkbox/Checkbox";
import { Autocomplete } from "../../ui/Autocomplete/Autocomplete";
import { MaskedInput } from "../../ui/MaskedInput/MaskedInput";
import { Button } from "../../ui/Button/Button";
import { Link } from "../Link/Link";
import { ButtonLink } from "../../ui/ButtonLink/ButtonLink";
import { Icon } from "../../ui/Icon/Icon";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../ui/Tooltip/Tooltip";
import {
  toast,
  ToastNotification,
} from "../../ui/ToastNotification/ToastNotification";
import { Loading } from "../../components/Loading/Loading";

import { provinces, getEducationDistributors } from "../../config";
// import { brands } from "../../config/brands";
import { set } from "lodash";
import config from "./config.json";

const phoneRegExp =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const schema = yup.object({
  salonName: yup.string().required().label("salon name"),
  salonAddress1: yup.string().required().label("salon address 1"),
  salonAddress2: yup.string().label("salon address 2"),
  province: yup.string().required(),
  city: yup.string().required(),
  postalCode: yup.string().required().label("postal code"),
  ownerContact: yup.string().required().label("owner/contact"),
  noOfStylists: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value
    )
    .required()
    .positive()
    .integer()
    .label("no. of stylists"),
  phone: yup
    .string()
    .matches(
      phoneRegExp,
      "Phone number is not valid.  Must have format 555-555-5555"
    )
    .required(),
  email: yup.string().email().required(),
  preferredMethodOfContact: yup
    .string()
    .label("preferred method of contact")
    .required(),
  distributor: yup
    .array()
    .min(1)
    .max(3)
    .of(
      yup.object({
        _id: yup
          .string()
          .nullable()
          .transform((value, originalValue) =>
            originalValue === "" ? null : value
          ),
        accountNumber: yup
          .string() //.required("Distributor account number is a required field")
          .label("account number"),
        salesRepName: yup.string().label("sales rep name"), // .required("Sales rep name is a required field"),
        salesRepEmail: yup.string().label("sales rep email"), //.required("Sales rep email is a required field")
        salesRepPhone: yup.string().label("sales rep phone"), //required("Sales rep phone is a required field"),
      })
    ),
  //brand: yup.string().required(),
  comments: yup.string(),
  brandFocus: yup.array().label("brands").required(),
  subBrandFocus: yup.array().label("sub brands"),
  typeOfClass: yup.string().label("type of class").required(),
  privateClass: yup.boolean().label("private class").default(false),
  //reasonForRequest: yup.string().required(),
});

const filterBrands = async ({ brands, inputValue }) => {
  const filteredBrands = brands
    .filter((i) => i.label.toLowerCase().includes(inputValue.toLowerCase()))
    .map((brand) => ({
      label: brand.label,
      value: brand.id,
    }));

  return filteredBrands;
};

const filterSubBrands = async ({ inputValue, selectedBrandIds = [] }) => {
  console.info({ selectedBrandIds, inputValue });
  const subBrands = config
    .filter((itm) => selectedBrandIds.includes(itm.brand))
    .flatMap((itm) =>
      itm.sub_brands.map((subBrand) => ({
        label: subBrand,
        value: subBrand,
        brand: itm.brand,
      }))
    );
  console.info({ subBrands });
  return subBrands;
};

const EDUCATION_REQUEST = gql`
  mutation EducationRequest($data: String!) {
    educationRequest(data: $data) {
      _id
    }
  }
`;

const GET_USER_DATA = gql`
  query GetDashboardData {
    me {
      user {
        _id
        dc
        dm
        avatar {
          media {
            _id
          }
        }
        firstname
        lastname
        phone
        email
        smsOptIn
        timezone
        data {
          promotions
          profession
          distributorSignup {
            _id
            accountNumber
          }
        }
        salon {
          _id
          n
          dc
          dm
          address {
            str1
            str2
            city
            state
            postal
          }
          data {
            salonBirthday
            website
          }
          ref {
            ext_id
            distributor {
              n
            }
          }
        }
      }
    }
  }
`;

const GET_SUB_BRANDS = gql`
  query GetSubBrands($startsWith: String!, $brandIds: [ID!]!) {
    brand {
      subBrands(startsWith: $startsWith, brandIds: $brandIds) {
        label
        value
        brand
      }
    }
  }
`;

export const NewRequest = () => {
  const intl = useIntl();
  const {
    loading: userDataLoading,
    error: userDataError,
    data: userData,
  } = useQuery(GET_USER_DATA);
  const user = userData?.me?.user || null;
  const brands = config.map((itm) => ({
    label: itm.brand,
    id: itm.brand,
  }));
  // console.info({ userData });
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    mode: "all", //"onTouched",
    resolver: yupResolver(schema),
    defaultValues: {
      salonId: "",
      salonName: "",
      salonAddress1: "",
      salonAddress2: "",
      province: "",
      city: "",
      postalCode: "",
      ownerContact: "",
      noOfStylists: "",
      phone: "",
      email: "",
      preferredMethodOfContact: "",
      distributor: [
        {
          _id: "",
          accountNumber: "",
          salesRepName: "",
          salesRepEmail: "",
          salesRepPhone: "",
        },
      ],
      brand: "",
      comments: "",
      brandFocus: [],
      subBrandFocus: [],
      typeOfClass: "",
      reasonForRequest: "",
      privateClass: false,
    },
  });

  // const blah = watch();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "distributor",
  });

  // Watch brandFocus to conditionally show subBrandFocus
  const brandFocusValue = watch("brandFocus");
  const subBrandFocusValue = watch("subBrandFocus");

  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [educationRequest] = useMutation(EDUCATION_REQUEST);

  useEffect(() => {
    setValue("salonId", user?.salon?._id || "");
    setValue("salonName", user?.salon?.n || "");
    setValue("salonAddress1", user?.salon?.address?.str1 || "");
    setValue("salonAddress2", user?.salon?.address?.str2 || "");
    setValue("province", user?.salon?.address?.state || "");
    setValue("city", user?.salon?.address?.city || "");
    setValue("postalCode", user?.salon?.address?.postal || "");
    setValue(
      "ownerContact",
      `${user?.firstname || ""} ${user?.lastname || ""}`.trim()
    );
  }, [user?._id]);

  useEffect(() => {
    console.info({ brandFocusValue, subBrandFocusValue });
    const filteredSubBrands = subBrandFocusValue.filter((item) =>
      brandFocusValue.map((itm) => itm.value).includes(item.brand)
    );
    setValue("subBrandFocus", filteredSubBrands);
  }, [brandFocusValue]);

  const onSubmit = async (data) => {
    if (!executeRecaptcha) {
      toast.error(
        <ToastNotification>
          <FormattedMessage defaultMessage="Invalid reCAPTCHA. Please try again." />
        </ToastNotification>
      );
      return;
    }

    setIsLoading(true);
    setSuccess(false);

    const captcha = await executeRecaptcha("education_request");
    console.log({ ...data, captcha });

    return educationRequest({
      variables: {
        data: JSON.stringify({ ...data, captcha }),
      },
    })
      .then((results) => {
        console.info({ results });
        setSuccess(results?.data?.educationRequest?._id);
        toast.success(
          <ToastNotification>
            <FormattedMessage defaultMessage="Your request has been submitted successfully!" />
          </ToastNotification>
        );
      })
      .catch((error) => {
        console.error("Error submitting education request:", error);
        toast.error(
          <ToastNotification>
            <FormattedMessage defaultMessage="There was an error submitting your request. Please try again later." />
          </ToastNotification>
        );
        setSuccess(false);
        // throw error; // Re-throw the error to handle it in the finally block
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // console.info({ isLoading, isSubmitting, isValid, errors, data: blah });
  return userDataLoading ? (
    <Loading global />
  ) : (
    <Root
      style={!user ? { marginTop: "50px", marginBottom: "100px" } : undefined}
    >
      {success ? (
        <SuccessContainer>
          <Typography type="h2">
            <FormattedMessage defaultMessage="Thank you for your request!" />
          </Typography>
          <Typography
            type="text-small"
            style={{ marginTop: 16, color: "#8f95a5" }}
          >
            Request #{success}
          </Typography>
          <Typography type="text-primary" style={{ marginTop: 32 }}>
            <FormattedMessage defaultMessage="Your request form was successfully submitted. We will review it and get back to your shortly." />
          </Typography>
          <Button
            as={Link}
            to="/rewards"
            variant="auth"
            arrow={false}
            style={{ marginTop: 56 }}
          >
            <FormattedMessage defaultMessage="browse rewards" />
          </Button>
          {user && (
            <ButtonLink
              as={Link}
              to="/dashboard"
              variant="secondary"
              style={{ marginTop: 32 }}
            >
              <FormattedMessage defaultMessage="Manage Dashboard" />
            </ButtonLink>
          )}
        </SuccessContainer>
      ) : (
        <Container>
          <Column>
            <Image src={imageSrc} alt="Hair coloring tools on a tray" />
          </Column>

          <Column>
            <Subtitle>
              {user && (
                <>
                  <Typography type="text-primary">
                    <b>
                      <FormattedMessage
                        defaultMessage="Hi {name},"
                        values={{ name: user.firstname }}
                      />
                    </b>
                  </Typography>
                  <br />
                </>
              )}
              <Typography type="text-primary">
                <FormattedMessage defaultMessage="Looking for product knowledge education on any of our brands? Please fill out the form below and a member of our education team will be in touch soon." />
              </Typography>
            </Subtitle>

            <Form onSubmit={handleSubmit(onSubmit)}>
              <div>
                {!user?.salon?._id && (
                  <>
                    <Controller
                      name="salonName"
                      control={control}
                      render={({ field }) => (
                        <TextInput
                          {...field}
                          label={intl.formatMessage({
                            defaultMessage: "Salon Name",
                          })}
                          error={errors?.salonName}
                        />
                      )}
                    />
                    <Controller
                      name="salonAddress1"
                      control={control}
                      render={({ field }) => (
                        <TextInput
                          {...field}
                          label={intl.formatMessage({
                            defaultMessage: "Salon Address 1",
                          })}
                          error={errors?.salonAddress1}
                        />
                      )}
                    />
                    <Controller
                      name="salonAddress2"
                      control={control}
                      render={({ field }) => (
                        <TextInput
                          {...field}
                          label={intl.formatMessage({
                            defaultMessage: "Salon Address 2 (optional)",
                          })}
                          error={errors?.salonAddress2}
                        />
                      )}
                    />
                    <Controller
                      name="province"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          control={control}
                          label={intl.formatMessage({
                            defaultMessage: "Province",
                          })}
                          options={provinces}
                          error={errors?.province}
                        />
                      )}
                    />
                    <Controller
                      name="city"
                      control={control}
                      render={({ field }) => (
                        <TextInput
                          {...field}
                          label={intl.formatMessage({
                            defaultMessage: "City",
                          })}
                          error={errors?.city}
                        />
                      )}
                    />
                    <Controller
                      name="postalCode"
                      control={control}
                      render={({ field }) => (
                        <TextInput
                          {...field}
                          label={intl.formatMessage({
                            defaultMessage: "Postal Code",
                          })}
                          error={errors?.postalCode}
                        />
                      )}
                    />
                    <Controller
                      name="ownerContact"
                      control={control}
                      render={({ field }) => (
                        <TextInput
                          {...field}
                          label={intl.formatMessage({
                            defaultMessage: "Owner/Contact",
                          })}
                          error={errors?.ownerContact}
                        />
                      )}
                    />
                  </>
                )}

                <Controller
                  name="noOfStylists"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      type="number"
                      min="1"
                      step="1"
                      label={intl.formatMessage({
                        defaultMessage: "No. of Stylists",
                      })}
                      error={errors?.noOfStylists}
                    />
                  )}
                />

                <Controller
                  name="phone"
                  render={({ field: { onChange, onBlur } }) => (
                    <MaskedInput
                      mask="000-000-0000"
                      label={intl.formatMessage({
                        defaultMessage: "Phone",
                      })}
                      error={errors?.phone}
                      onBlur={onBlur}
                      onChange={(v) => onChange(v)}
                    />
                  )}
                  control={control}
                />
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      type="email"
                      label={intl.formatMessage({
                        defaultMessage: "Email",
                      })}
                      autoComplete="email"
                      error={errors?.email}
                    />
                  )}
                />
                <Controller
                  name="preferredMethodOfContact"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      control={control}
                      label={intl.formatMessage({
                        defaultMessage: "Preferred Method of Contact",
                      })}
                      options={[
                        {
                          label: "Phone",
                          value: "phone",
                        },
                        {
                          label: "Text",
                          value: "text",
                        },
                        {
                          label: "Email",
                          value: "email",
                        },
                      ]}
                      error={errors?.preferredMethodOfContact}
                    />
                  )}
                />
              </div>

              <Divider />

              <DistributorContainer>
                <DistributorList>
                  {fields.map((item, index) => (
                    <li key={item.id}>
                      <DistributorHeader>
                        <span style={{ display: "flex", alignItems: "center" }}>
                          <DistributorTitle>
                            <FormattedMessage defaultMessage="Distributor" />{" "}
                            {index + 1}
                          </DistributorTitle>
                          {/* <Tooltip placement="top-start">
                            <TooltipTrigger>
                              <DistributorTooltip>
                                <Icon icon="info-circle" />
                              </DistributorTooltip>
                            </TooltipTrigger>
                            <TooltipContent style={{ width: 180 }}>
                              <FormattedMessage defaultMessage="Lorem Ipsum is simply dummy text of the printing and typesetting industry." />
                            </TooltipContent>
                          </Tooltip> */}
                        </span>
                        {index > 0 && (
                          <DistributorRemoveButton
                            type="button"
                            onClick={() => remove(index)}
                          >
                            <Icon icon="trash-can" />
                          </DistributorRemoveButton>
                        )}
                      </DistributorHeader>
                      <Controller
                        name={`distributor.${index}._id`}
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            control={control}
                            label={intl.formatMessage({
                              defaultMessage: "Distributor",
                            })}
                            options={getEducationDistributors()}
                            error={errors?.distributor?.[index]?._id}
                          />
                        )}
                      />
                      <Controller
                        name={`distributor.${index}.accountNumber`}
                        control={control}
                        render={({ field }) => (
                          <TextInput
                            {...field}
                            label={intl.formatMessage({
                              defaultMessage: "Distributor Account Number",
                            })}
                            error={errors?.distributor?.[index]?.accountNumber}
                          />
                        )}
                      />
                      <Controller
                        name={`distributor.${index}.salesRepName`}
                        control={control}
                        render={({ field }) => (
                          <TextInput
                            {...field}
                            label={intl.formatMessage({
                              defaultMessage: "Sales Rep Name",
                            })}
                            error={errors?.distributor?.[index]?.salesRepName}
                          />
                        )}
                      />
                      <Controller
                        name={`distributor.${index}.salesRepEmail`}
                        control={control}
                        render={({ field }) => (
                          <TextInput
                            {...field}
                            label={intl.formatMessage({
                              defaultMessage: "Sales Rep Email",
                            })}
                            error={errors?.distributor?.[index]?.salesRepEmail}
                          />
                        )}
                      />
                      <Controller
                        name={`distributor.${index}.salesRepPhone`}
                        control={control}
                        render={({ field }) => (
                          <TextInput
                            {...field}
                            label={intl.formatMessage({
                              defaultMessage: "Sales Rep Phone",
                            })}
                            error={errors?.distributor?.[index]?.salesRepPhone}
                          />
                        )}
                      />
                    </li>
                  ))}
                </DistributorList>
                <Button
                  variant="action"
                  arrow={false}
                  type="button"
                  onClick={() =>
                    append({
                      _id: "",
                      accountNumber: "",
                      salesRepName: "",
                      salesRepEmail: "",
                      salesRepPhone: "",
                    })
                  }
                >
                  <Icon icon="plus" style={{ marginRight: 6, fontSize: 16 }} />
                  <FormattedMessage
                    defaultMessage="add new distributor"
                    tagName="span"
                  />
                </Button>
              </DistributorContainer>

              <Divider />

              <div>
                <Note>
                  <FormattedMessage defaultMessage="Please provide additional information about the type of education you are requesting:" />
                </Note>
                {/* <Controller
                  name="brand"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      control={control}
                      label={intl.formatMessage({
                        defaultMessage: "Brand",
                      })}
                      options={brands.map((brand) => ({
                        label: brand.label,
                        value: brand.id,
                      }))}
                      error={errors?.brand}
                    />
                  )}
                />

                */}

                <Controller
                  name="brandFocus"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      isAsync
                      isMulti
                      cacheOptions
                      defaultOptions
                      loadOptions={(inputValue) =>
                        filterBrands({
                          brands,
                          inputValue,
                        })
                      }
                      error={errors?.brandFocus}
                      placeholder={intl.formatMessage({
                        defaultMessage: "Brand(s) Focus",
                      })}
                      {...field}
                    />
                  )}
                />

                {brandFocusValue && brandFocusValue.length > 0 && (
                  <Controller
                    name="subBrandFocus"
                    control={control}
                    render={({ field }) => {
                      const selectedBrandIds = brandFocusValue.map(
                        (item) => item.value
                      );

                      return (
                        <Autocomplete
                          isAsync
                          isMulti
                          cacheOptions
                          defaultOptions
                          key={selectedBrandIds.join("-")}
                          loadOptions={(inputValue) =>
                            filterSubBrands({
                              brands,
                              inputValue,
                              selectedBrandIds,
                            })
                          }
                          error={errors?.subBrandFocus}
                          placeholder={intl.formatMessage({
                            defaultMessage: "Sub-Brand(s) Focus",
                          })}
                          {...field}
                        />
                      );
                    }}
                  />
                )}

                <Controller
                  name="typeOfClass"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      control={control}
                      label={intl.formatMessage({
                        defaultMessage: "Type of Class Requested",
                      })}
                      options={[
                        {
                          label: "Virtual",
                          value: "virtual",
                        },
                        {
                          label: "In-Salon",
                          value: "in-salon",
                        },
                      ]}
                      error={errors?.typeOfClass}
                    />
                  )}
                />
                {/* <Controller
                  name="reasonForRequest"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      label={intl.formatMessage({
                        defaultMessage: "Reason for Request",
                      })}
                      error={errors?.reasonForRequest}
                    />
                  )}
                /> */}

                <Controller
                  name="comments"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      label={intl.formatMessage({
                        defaultMessage: "Reason for Request",
                      })}
                      error={errors?.comments}
                      rows={4}
                    />
                  )}
                />

                <Controller
                  name="privateClass"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      control={control}
                      type="checkbox"
                      error={errors?.privateClass}
                      label={intl.formatMessage({
                        defaultMessage:
                          "Private Class with Artist - Fees and/or ClubH Points May Apply",
                      })}
                    />
                  )}
                />
              </div>

              <Button
                type="submit"
                variant="auth"
                arrow={false}
                disabled={isLoading || isSubmitting || !isValid}
              >
                <FormattedMessage defaultMessage="submit my request" />
              </Button>
            </Form>
          </Column>
        </Container>
      )}
    </Root>
  );
};
